import React, { useEffect, useState } from 'react';
import api from '../api/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { FileText, FilePdf, FileDoc, Eye, ArrowClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

const AdminDocsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/admin/docs');
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore caricamento documentazione');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const download = async (key, format) => {
    setBusy(`${key}-${format}`);
    try {
      const r = await api.get(`/api/admin/docs/${key}/download?format=${format}`, { responseType: 'blob' });
      const disp = r.headers['content-disposition'] || '';
      const match = disp.match(/filename="?([^"]+)"?/);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = match ? match[1] : `${key}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download avviato');
    } catch (e) {
      toast.error('Errore durante il download');
    } finally { setBusy(''); }
  };

  const openPreview = async (key) => {
    try {
      const r = await api.get(`/api/admin/docs/${key}/preview`);
      setPreview(r.data);
    } catch (e) {
      toast.error('Anteprima non disponibile');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 py-12" data-testid="admin-docs-page">
        <div className="mb-8 flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading font-semibold text-4xl mb-2">Documentazione</h1>
            <p className="text-foreground/70">
              Scheda tecnica per investitori e manuali d'uso per ogni ruolo. I documenti vengono aggiornati
              ad ogni nuova funzionalità rilasciata.
            </p>
          </div>
          <Button variant="outline" className="rounded-full" onClick={load} data-testid="docs-refresh-btn">
            <ArrowClockwise size={15} className="mr-1.5" /> Aggiorna
          </Button>
        </div>

        {loading ? (
          <p className="text-foreground/60" data-testid="admin-docs-loading">Caricamento documenti…</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {items.map((d) => (
              <Card key={d.key} className="rounded-2xl p-5 flex flex-col gap-3" data-testid={`doc-card-${d.key}`}>
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <FileText size={22} weight="duotone" className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-heading font-medium text-lg leading-tight">{d.title}</p>
                    <p className="text-sm text-foreground/70 mt-0.5">{d.subtitle}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px]">{d.audience}</Badge>
                      <span className="text-[11px] text-foreground/55">
                        {d.updated_at ? `Aggiornato il ${new Date(d.updated_at).toLocaleDateString('it-IT')}` : 'Non disponibile'}
                        {d.size_kb ? ` · ${d.size_kb} KB` : ''}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 pt-1 border-t border-border/60">
                  <Button size="sm" className="rounded-full h-8" disabled={!d.available || busy === `${d.key}-pdf`}
                    onClick={() => download(d.key, 'pdf')} data-testid={`doc-pdf-${d.key}`}>
                    <FilePdf size={15} className="mr-1.5" /> PDF
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full h-8" disabled={!d.available || busy === `${d.key}-docx`}
                    onClick={() => download(d.key, 'docx')} data-testid={`doc-docx-${d.key}`}>
                    <FileDoc size={15} className="mr-1.5" /> Word
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-full h-8" disabled={!d.available}
                    onClick={() => openPreview(d.key)} data-testid={`doc-preview-${d.key}`}>
                    <Eye size={15} className="mr-1.5" /> Anteprima
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="doc-preview-dialog">
          <DialogHeader>
            <DialogTitle>{preview?.title}</DialogTitle>
          </DialogHeader>
          <pre className="whitespace-pre-wrap text-[13px] leading-relaxed font-sans text-foreground/85" data-testid="doc-preview-content">
            {preview?.markdown}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDocsPage;
